import Decimal from 'decimal.js';
import { getOptionSplitMultiplier } from './getOptionSplitMultiplier.js';
import { validateNativeQuantity } from './nativeQuantity.js';
/**
 * How a transaction changes the quantity of the position it is recorded against. The shared trade type lists do not
 * describe split transfers or contract settlement, so every transaction type is classified here.
 *
 * Expiration, assignment and exercise settle the whole option contract. Expiration rows carry the remaining quantity,
 * but assignment and exercise markers are recorded with 0 quantity and are the only settlement row after an early
 * assignment. Settlement stock rows (`assignmentBuy`, `buy`, `shortSell`, `sell`, `buyToCover`) have no derivative and
 * form a separate share position.
 */
const POSITION_EFFECTS = {
    buy: 'increase',
    buyToOpen: 'increase',
    sellToOpen: 'increase',
    shortSell: 'increase',
    assignmentBuy: 'increase',
    splitIn: 'increase',
    sell: 'decrease',
    sellToClose: 'decrease',
    buyToClose: 'decrease',
    buyToCover: 'decrease',
    splitOut: 'decrease',
    expiration: 'settle',
    assignment: 'settle',
    exercise: 'settle',
    dividend: 'none',
    dividendReinvestment: 'none'
};
const QuantityDecimal = Decimal.clone({ precision: 80 });
const assertOptionSplitPairing = (legTransactions, index) => {
    const row = legTransactions[index];
    if (row.type === 'splitOut') {
        const incoming = legTransactions[index + 1];
        if (!incoming)
            throw new Error('Unpaired option split transfer');
        getOptionSplitMultiplier(row, incoming);
    }
    if (row.type === 'splitIn' && legTransactions[index - 1]?.type !== 'splitOut') {
        throw new Error('Unpaired option split transfer');
    }
};
/**
 * A trade group is complete when every position it holds is flat. A position is one leg's option contract or that
 * leg's underlying units, so an entry filled in several executions, a partial close, and assigned shares that are still held all
 * leave the group open. Transactions must be in execution order for option split pairing.
 */
export const checkTradeGroupForCompletion = (transactionsInGroup) => {
    const transactionsByLeg = new Map();
    for (const transaction of transactionsInGroup) {
        const legTransactions = transactionsByLeg.get(transaction.legId);
        if (legTransactions)
            legTransactions.push(transaction);
        else
            transactionsByLeg.set(transaction.legId, [transaction]);
    }
    for (const [legId, legTransactions] of transactionsByLeg) {
        const balances = new Map();
        let hasUnidentifiedSettlement = false;
        for (const [index, row] of legTransactions.entries()) {
            assertOptionSplitPairing(legTransactions, index);
            const effect = POSITION_EFFECTS[row.type];
            if (row.derivativeType === 'Future' &&
                (!Number.isSafeInteger(row.derivativeId) ||
                    Number(row.derivativeId) <= 0 ||
                    !['buyToOpen', 'sellToOpen', 'sellToClose', 'buyToClose'].includes(row.type))) {
                throw new Error('Futures completion requires dated contracts and native opening/closing actions');
            }
            if (effect === 'none')
                continue;
            const quantity = validateNativeQuantity(Number(row.quantity), row, { allowZero: true });
            const identity = row.derivativeId ?? null;
            // Live assignment markers recorded before May 2026 have no derivative identity; they settle the leg's contract.
            if (effect === 'settle' && identity === null) {
                hasUnidentifiedSettlement = true;
                continue;
            }
            const balance = balances.get(identity) ?? { quantity: new QuantityDecimal(0), isSettled: false };
            if (effect === 'settle')
                balance.isSettled = true;
            else
                balance.quantity = effect === 'increase' ? balance.quantity.plus(quantity) : balance.quantity.minus(quantity);
            balances.set(identity, balance);
        }
        if (hasUnidentifiedSettlement && ![...balances.keys()].some((identity) => identity !== null)) {
            throw new Error(`Leg ${legId} has an option settlement transaction but no option contract`);
        }
        for (const [identity, { quantity, isSettled }] of balances) {
            const isContractSettled = isSettled || (hasUnidentifiedSettlement && identity !== null);
            if (!isContractSettled && !quantity.isZero())
                return false;
        }
    }
    return true;
};
//# sourceMappingURL=checkTradeGroupForCompletion.js.map